import { MapPin } from 'lucide-react';
import type { OpportunityCluster } from '../../types';

interface ClusterTableProps {
  clusters: OpportunityCluster[];
  selectedClusterId: string | null;
  onSelectCluster: (id: string) => void;
  onViewTerritory: (id: string) => void;
}

function ScoreColor({ score }: { score: number }) {
  const color = score >= 70 ? 'text-red-600 dark:text-red-400' : score >= 40 ? 'text-orange-600 dark:text-orange-400' : 'text-gray-600 dark:text-gray-400';
  return <span className={`text-lg font-bold ${color}`}>{score}</span>;
}

export default function ClusterTable({ clusters, selectedClusterId, onSelectCluster, onViewTerritory }: ClusterTableProps) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm flex flex-col h-full">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100">
          Opportunity Clusters <span className="text-sm font-normal text-gray-500 dark:text-gray-400">({clusters.length})</span>
        </h3>
      </div>

      <div className="flex-1 overflow-y-auto">
        {clusters.length === 0 ? (
          <div className="p-4 text-sm text-gray-500 dark:text-gray-400 text-center">
            No clusters found. Try adjusting settings.
          </div>
        ) : (
          clusters.map((cluster) => {
            const isSelected = cluster.id === selectedClusterId;
            const total = cluster.totalSchools || 1;
            const blastW = (cluster.blastCount / total) * 100;
            const compW = (cluster.competitorCount / total) * 100;
            const openW = (cluster.openCount / total) * 100;

            return (
              <div
                key={cluster.id}
                onClick={() => onSelectCluster(cluster.id)}
                className={`p-4 border-b border-gray-100 dark:border-gray-700/50 cursor-pointer transition-all hover:bg-gray-50 dark:hover:bg-gray-700/30 ${
                  isSelected ? 'ring-2 ring-blue-500 ring-inset bg-blue-50/50 dark:bg-blue-900/20' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                    <span className="text-sm font-bold text-gray-700 dark:text-gray-300">#{cluster.rank}</span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate">{cluster.label}</span>
                      <ScoreColor score={cluster.opportunityScore} />
                    </div>

                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {cluster.totalSchools.toLocaleString()} schools &middot; {cluster.penetrationPct}% penetration
                    </div>

                    <div className="flex h-1.5 rounded-full overflow-hidden mt-2 bg-gray-100 dark:bg-gray-700">
                      <div className="bg-green-500" style={{ width: `${blastW}%` }} />
                      <div className="bg-red-500" style={{ width: `${compW}%` }} />
                      <div className="bg-gray-400" style={{ width: `${openW}%` }} />
                    </div>

                    <button
                      onClick={(e) => { e.stopPropagation(); onViewTerritory(cluster.id); }}
                      className="mt-2 flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
                    >
                      <MapPin className="h-3 w-3" />
                      View Territory
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
